import { type GridSelectionModel } from '@mui/x-data-grid-pro';
import omit from 'lodash-es/omit';
import xorWith from 'lodash-es/xorWith';

import type Order from '~/types/Order';

import { ActionKeys } from '~/actions/constants';
import { type ActionTypes } from '~/actions/index';
import { type CountryIso2 } from '~/constants/countries';
import { IDVerificationStatus } from '~/types/IDVerification';
import { sortOrderTagsByName, type OrderObjectId, type OrderSelection } from '~/types/Order';
import { type BatchEntryExtended } from '~/types/OrderBatchEntry';
import { getLabelUrl } from '~/types/Transaction';
import { getFailed, getNotRequested, getRequesting, getSuccess } from '~/utils/fetchable';
import { listToMapById } from '~/utils/list';
import { newRelic } from '~/utils/newRelic/newRelic';
import { isUpdatingParcel } from '~/utils/order';

type LabelsStatus = Requestable<string, string>;
type PackingSlipsStatus = Requestable<string, string>;
export type NotificationStatus = AutomatedNotifications | Requestable<null, string>;

type OrderEdit = {
  kind: 'order-edit';
  orderSelection: OrderSelection;
};

type CreditCardInfo = {
  kind: 'credit-card-info';
};

type UpsActivation = {
  accountCountry?: CountryIso2;
  kind: 'ups-activation';
  showFullForm?: boolean;
};

type CanadaPostActivation = {
  kind: 'canada_post-activation';
  showFullForm?: boolean;
};

type DHLExpressActivation = {
  kind: 'dhl_express-activation';
};

type RoyalMailSFActivation = {
  kind: 'royal_mail_sf-activation';
};

type AccountAlert = {
  kind: 'account-alert';
};

type Confirmation = {
  kind: 'confirmation';
  request: Requestable<null, string>;
};

type PreConfirmation = {
  hasAutomatedEmailNotifications: boolean;
  kind: 'email-notification';
};

type EmailVerification = {
  kind: 'email-verification';
};

type EmailNotification = {
  kind: 'email-notification';
  notifications: AutomatedNotifications | Requestable<null, string>;
};

type AutomatedNotifications = {
  kind: 'automated-notifications';
};

type MergedDocuments = {
  kind: 'documents-merged';
  labels: LabelsStatus;
};

type Documents = {
  kind: 'documents';
  labels: LabelsStatus;
  packingSlips: PackingSlipsStatus;
};

export type DocumentStatus = Documents | MergedDocuments;

export type DownloadLabels = {
  documents: DocumentStatus;
  kind: 'download-labels';
  notifications: NotificationStatus;
};

export type DownloadLabelsWithBatchErrors = {
  documents: DocumentStatus;
  kind: 'download-labels-with-batch-errors';
  notifications: NotificationStatus;
};

type CheckoutForNotification = {
  currentStep: EmailNotification;
  kind: 'email-notification';
};

export type PreCheckoutSteps =
  | AccountAlert
  | CanadaPostActivation
  | CreditCardInfo
  | DHLExpressActivation
  | EmailVerification
  | PreConfirmation
  | RoyalMailSFActivation
  | UpsActivation;

export type Checkout = {
  currentStep:
    | AutomatedNotifications
    | Confirmation
    | DownloadLabels
    | DownloadLabelsWithBatchErrors
    | EmailNotification;
  kind: 'checkout';
  orders: OrderSelection;
  preCheckoutQueue: PreCheckoutSteps[];
};

type Hiding = {
  kind: 'hide';
  request: Requestable<null, string>;
};

export type IDVerificationState = {
  kind: 'id-verification';
  // Using a Requestable to track the status of the ID Verification
  status: Requestable<null, { codes: string[]; retryable: boolean }>;
};

export type SplittingStatus = Requestable<null, string>;

type Splitting = {
  kind: 'splitting';
  orderObjectId: OrderObjectId;
  status: SplittingStatus;
};

type NoAction = {
  kind: 'no-action';
};

export type OrdersActions =
  | Checkout
  | CheckoutForNotification
  | Hiding
  | IDVerificationState
  | NoAction
  | OrderEdit
  | Splitting;

export type CheckoutRateAfterPollAttributes = {
  autoStartCheckout: boolean;
  carrierAccount: string;
  currBatchEntry: Maybe<BatchEntryExtended>;
  prevBatchEntry: BatchEntryExtended;
  serviceLevelToken: string;
};

export type CheckoutRatesAfterPoll = Record<string, CheckoutRateAfterPollAttributes>;

export const checkoutRateAfterPollKey = (batchEntry: BatchEntryExtended): string =>
  `${batchEntry.orderObjectId}:${batchEntry.partialFulfillmentRank}`;

export type OrdersState = {
  readonly allIds: readonly string[];
  readonly allOrdersCount: number;
  readonly autoSaveErrors: Record<OrderObjectId, boolean>;
  readonly checkedOrders: OrderSelection;
  readonly checkoutRateAfterPoll: Maybe<CheckoutRatesAfterPoll>;
  readonly createReturnBatchEntryHasError: boolean;
  readonly data: { [orderId: string]: Readonly<Order> };
  readonly fetchOrderHasError: boolean;
  readonly hasNewOrders: boolean;
  readonly isCreatingCustomsItems: boolean;
  readonly isCreatingReturnBatchEntry: boolean;
  readonly isLoading: boolean;
  readonly isLoadingOrder: boolean; // single order fetch
  readonly isLoadingOrderTags: boolean;
  readonly isSidePanelCheckout: boolean;
  readonly isSingleOrderMode: boolean;
  readonly isSyncingOrders: boolean;
  // These are the orders in a batch successfully
  // got purchased but the user couldn't get to
  readonly isUpdatingBatchEntry: boolean;
  readonly isUpdatingCustomsItems: boolean;
  readonly isUpdatingParcel: boolean;
  readonly lastOrderId: string;
  readonly orderAction: OrdersActions;
  readonly orderTagsBeingUpdated: OrderObjectId[];
  // download the labels because other orders failed.
  readonly ordersRemainingLabelDownload: OrderSelection;
  readonly patchBatchEntriesExtrasRequest: Requestable<BatchEntryExtended[], string>;
  readonly patchBatchEntriesRequest: Requestable<BatchEntryExtended[], string>;
  readonly returnLabelCreatedSuccessfully: boolean;
  readonly rowModelBeingEdited: GridSelectionModel;
  readonly selectedOrders: OrderSelection;
  readonly totalOrders: number;
};

export const initialState: OrdersState = {
  allIds: [],
  allOrdersCount: 0,
  autoSaveErrors: {},
  checkedOrders: {},
  checkoutRateAfterPoll: null,
  createReturnBatchEntryHasError: false,
  data: {},
  fetchOrderHasError: false,
  hasNewOrders: false,
  isCreatingCustomsItems: false,
  isCreatingReturnBatchEntry: false,
  isLoading: false,
  isLoadingOrder: false,
  isLoadingOrderTags: false,
  isSidePanelCheckout: false,
  isSingleOrderMode: false,
  isSyncingOrders: false,
  isUpdatingBatchEntry: false,
  isUpdatingCustomsItems: false,
  isUpdatingParcel: false,
  lastOrderId: '',
  orderAction: { kind: 'no-action' },
  orderTagsBeingUpdated: [],
  ordersRemainingLabelDownload: {},
  patchBatchEntriesExtrasRequest: getNotRequested(),
  patchBatchEntriesRequest: getNotRequested(),
  returnLabelCreatedSuccessfully: false,
  rowModelBeingEdited: [],
  selectedOrders: {},
  totalOrders: 0,
};

const reducer = (state: OrdersState = initialState, action: ActionTypes): OrdersState => {
  switch (action.type) {
    case ActionKeys.ORDER_FETCH:
      return {
        ...state,
        fetchOrderHasError: false,
        isLoadingOrder: true,
      };
    case ActionKeys.ORDER_FETCH_SUCCESS:
      const currentOrderObject = state.data[action.payload.order.objectId];
      const orderIdIndex = state.allIds.findIndex((id) => id === action.payload.order.objectId);
      const currentTags = currentOrderObject?.tags;

      const returnBatchEntries =
        currentOrderObject?.partialFulfillmentRequests.filter(({ isReturn }) => isReturn) || [];

      return {
        ...state,
        allIds:
          orderIdIndex === -1 ? [...state.allIds, action.payload.order.objectId] : state.allIds,
        data: {
          ...state.data,
          /*
           * We need to merge the tags from the current order state with the new order until
           * ORDER_FETCH_SUCCESS and ORDERS_FETCH_SUCCESS return the array of tags in them,
           * to avoid losing the tags when users use any bulk action.
           */
          [action.payload.order.objectId]: {
            ...action.payload.order,
            partialFulfillmentRequests: [
              ...action.payload.order.partialFulfillmentRequests,
              ...returnBatchEntries,
            ],
            tags: currentTags,
          },
        },
        isLoadingOrder: false,
      };

    case ActionKeys.ORDER_FETCH_ERROR:
      return { ...state, fetchOrderHasError: true, isLoadingOrder: false };

    case ActionKeys.ORDER_CLEAN: {
      return {
        ...state,
        allIds: state.allIds.filter((id) => id !== action.orderId),
        data: omit(state.data, action.orderId),
      };
    }

    case ActionKeys.ORDERS_FETCH:
      return {
        ...state,
        allIds: [],
        isLoading: true,
        selectedOrders: {},
      };
    case ActionKeys.ORDERS_FETCH_SUCCESS:
      try {
        return {
          ...state,
          allIds: action.payload.orders.map((order: Order) => order.objectId),
          data: {
            ...state.data,
            ...action.payload.orders.reduce(
              (obj, order: Order) => ({ ...obj, [order.objectId]: order }),
              {},
            ),
          },
          hasNewOrders: false,
          isLoading: false,
          isSyncingOrders: false,
          // TODO: Removing this will support multi page selections
          selectedOrders: {},
          totalOrders: action.payload.totalOrders,
        };
      } catch (error) {
        newRelic.noticeError('Failed to update orders after fetch', { error });

        return {
          ...state,
          allIds: [],
          isLoading: false,
          isSyncingOrders: false,
          selectedOrders: {},
          totalOrders: 0,
        };
      }
    case ActionKeys.ORDERS_FETCH_ERROR:
      return { ...state, isLoading: false };
    case ActionKeys.ORDERS_SYNC:
      return {
        ...state,
        isSyncingOrders: true,
      };
    case ActionKeys.ORDERS_SYNC_SUCCESS:
      return {
        ...state,
        hasNewOrders: false,
        isSyncingOrders: false,
      };
    case ActionKeys.ORDERS_SYNC_ERROR:
      return {
        ...state,
        isSyncingOrders: false,
      };
    case ActionKeys.ORDERS_SAVE_LAST_ORDER_ID:
      return {
        ...state,
        lastOrderId: action.lastOrderId,
      };
    case ActionKeys.ORDERS_NEW_ORDERS_FOUND:
      return {
        ...state,
        hasNewOrders: true,
        lastOrderId: action.newOrderId,
      };
    case ActionKeys.ORDER_HIDE:
      return {
        ...state,
        orderAction: {
          kind: 'hide',
          request: getRequesting(),
        },
      };
    case ActionKeys.ORDER_HIDE_SUCCESS:
      if (state.orderAction.kind !== 'hide') return state;

      return {
        ...state,
        orderAction: {
          kind: 'hide',
          request: getSuccess(null),
        },
      };
    case ActionKeys.ORDER_HIDE_ERROR:
      if (state.orderAction.kind !== 'hide') return state;

      return {
        ...state,
        orderAction: {
          kind: 'hide',
          request: getFailed(action.payload.error),
        },
      };
    case ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS_PACKING_SLIP_NOTE:
      return {
        ...state,
        patchBatchEntriesExtrasRequest: getRequesting(),
      };
    case ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS_SUCCESS:
    case ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_EXTRAS_SUCCESS:
    case ActionKeys.ORDERS_PATCH_BATCH_ENTRIES_SUCCESS:
    case ActionKeys.ORDERS_UPDATE_PARCEL_SUCCESS:
      const newData = Object.keys(state.data).reduce((obj, orderObjectId) => {
        const batchEntry = action.payload.batchEntries.find(
          (batchEntry) => batchEntry.orderObjectId === orderObjectId,
        );
        if (!batchEntry) return { ...obj, [orderObjectId]: state.data[orderObjectId] };

        return {
          ...obj,
          [orderObjectId]: { ...state.data[orderObjectId], parcel: batchEntry.parcel },
        };
      }, {});

      const newSelectedOrders: Record<string, number[]> = {};
      action.payload.batchEntries.forEach((batchEntry) => {
        // if a selected order had no batch entries, and batch entries have just been created for that order
        // by adding packages, select all of the new batch entries
        if (state.selectedOrders[batchEntry.orderObjectId]?.length === 0) {
          newSelectedOrders[batchEntry.orderObjectId] =
            newSelectedOrders[batchEntry.orderObjectId] || [];
          newSelectedOrders[batchEntry.orderObjectId].push(batchEntry.id);
        }
      });

      return {
        ...state,
        autoSaveErrors:
          ActionKeys.ORDERS_UPDATE_PARCEL_SUCCESS === action.type
            ? {
                ...state.autoSaveErrors,
                ...Object.keys(state.selectedOrders).reduce((current, orderId) => {
                  current[orderId] = false;
                  return current;
                }, {}),
              }
            : state.autoSaveErrors,
        data: newData,
        isUpdatingBatchEntry: false,
        isUpdatingParcel: false,
        patchBatchEntriesExtrasRequest:
          action.type === ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS_SUCCESS
            ? getSuccess(action.payload.batchEntries)
            : state.patchBatchEntriesExtrasRequest,
        patchBatchEntriesRequest:
          action.type === ActionKeys.ORDERS_PATCH_BATCH_ENTRIES_SUCCESS
            ? getSuccess(action.payload.batchEntries)
            : state.patchBatchEntriesRequest,
        selectedOrders: { ...state.selectedOrders, ...newSelectedOrders },
      };
    case ActionKeys.ORDERS_SELECT_ORDER: {
      const orderSelection = action.payload;

      const newSelection = Object.keys(orderSelection).reduce(
        (obj, orderId) => {
          const newBeSelection = orderSelection[orderId];
          const oldBeSelection = obj[orderId];

          const selectionOrder = state.data[orderId];

          const returnFulfillmentRequest = selectionOrder?.partialFulfillmentRequests?.find(
            ({ returnOfID }) => newBeSelection[0] === returnOfID,
          );

          if (returnFulfillmentRequest) return { ...obj, [orderId]: [returnFulfillmentRequest.id] };

          // If there was no old selection or the old selection was partial, replace with incoming
          if (!oldBeSelection || oldBeSelection.length < newBeSelection.length)
            return { ...obj, [orderId]: newBeSelection };

          if (newBeSelection.length === 0) return omit(obj, [orderId]);

          const diff = xorWith(
            oldBeSelection,
            newBeSelection,
            (arrVal: number, othVal: number) => arrVal === othVal,
          );

          if (diff.length === 0) return omit(obj, [orderId]);

          return {
            ...obj,
            [orderId]: diff,
          };
        },
        { ...state.selectedOrders },
      );

      return {
        ...state,
        createReturnBatchEntryHasError: false,
        isCreatingReturnBatchEntry: false,
        selectedOrders: newSelection,
      };
    }
    case ActionKeys.ORDERS_SELECT_CHECKED_ORDER:
      const orderSelection = action.payload;

      const newSelection = Object.keys(orderSelection).reduce(
        (obj, orderId) => {
          const newBeSelection = orderSelection[orderId];
          const oldBeSelection = obj[orderId];

          // If there was no old selection or the old selection was partial, replace with incoming
          if (!oldBeSelection || oldBeSelection.length < newBeSelection.length)
            return { ...obj, [orderId]: newBeSelection };

          if (newBeSelection.length === 0) return omit(obj, [orderId]);

          const diff = xorWith(
            oldBeSelection,
            newBeSelection,
            (arrVal: number, othVal: number) => arrVal === othVal,
          );

          if (diff.length === 0) return omit(obj, [orderId]);

          return {
            ...obj,
            [orderId]: diff,
          };
        },
        { ...state.checkedOrders },
      );

      return {
        ...state,
        checkedOrders: newSelection,
      };
    /**
     * If the payload is sent empty then clear the storage
     */
    case ActionKeys.ORDERS_REMAINING_LABEL_DOWNLOAD:
      return {
        ...state,
        ordersRemainingLabelDownload: action.payload || {},
      };
    case ActionKeys.ORDERS_CLEAR_CHECKED_ORDERS:
      return {
        ...state,
        checkedOrders: {},
      };
    case ActionKeys.ORDERS_INITIATE_BULK_EDIT:
      // sets selectedOrders appropriately from the download toolbar
      return {
        ...state,
        rowModelBeingEdited: [], // clear out the rowModelBeingEdited remove single-order edit
        selectedOrders: action.payload,
      };
    case ActionKeys.ORDERS_SYNC_SELECTED_ORDERS:
      return {
        ...state,
        selectedOrders: { ...state.selectedOrders, ...action.payload },
      };
    case ActionKeys.ORDERS_UPDATE_ROW_MODEL_BEING_EDITED:
      return {
        ...state,
        rowModelBeingEdited: action.payload,
      };
    case ActionKeys.ORDERS_CLEAR_ROW_MODEL_BEING_EDITED:
      return {
        ...state,
        rowModelBeingEdited: [],
      };
    case ActionKeys.ORDERS_CLEAR_BATCH_ENTRIES: {
      const newRankMap = action.payload.resetBatchEntries.reduce<OrderSelection>(
        (obj, batchEntry) => ({
          ...obj,
          [batchEntry.orderObjectId]: {
            ...obj[batchEntry.orderObjectId],
            [batchEntry.partialFulfillmentRank]: batchEntry.id,
          },
        }),
        {},
      );

      const oldBeMap = listToMapById(action.payload.initialBatchEntries);

      const newSelection = Object.keys(state.selectedOrders).reduce<OrderSelection>(
        (obj, orderObjectId) => ({
          ...obj,
          [orderObjectId]: state.selectedOrders[orderObjectId].map((beId) => {
            const oldBe = oldBeMap[beId] as BatchEntryExtended;
            // Guard in case this is misused.
            // In theory the batch entries already selected this
            // will always be in the initalBatchEntries list.
            if (!oldBe) return beId;
            const oldRank = oldBe.partialFulfillmentRank;
            const newBeId = newRankMap[orderObjectId]?.[oldRank];

            return newBeId || beId;
          }),
        }),
        {},
      );

      return {
        ...state,
        selectedOrders: newSelection,
      };
    }
    case ActionKeys.ORDERS_CLEAR_SELECTED:
      return {
        ...state,
        selectedOrders: {},
      };
    case ActionKeys.ORDERS_COUNT_SUCCESS:
      return {
        ...state,
        allOrdersCount: action.payload.count,
      };

    // CHECKOUT
    case ActionKeys.ORDERS_CHECKOUT_TOGGLE:
      return {
        ...state,
        orderAction:
          state.orderAction.kind !== 'checkout' && state.orderAction.kind !== 'email-notification'
            ? {
                currentStep: { kind: 'confirmation', request: getNotRequested() },
                kind: 'checkout',
                orders: action.payload.orders,
                preCheckoutQueue: action.payload.preCheckoutQueue,
              }
            : state.orderAction.kind === 'checkout' && action.payload.preCheckoutQueue.length > 0
            ? {
                currentStep: {
                  kind: 'email-notification',
                  notifications: (action.payload.preCheckoutQueue[0] as PreConfirmation)
                    .hasAutomatedEmailNotifications
                    ? { kind: 'automated-notifications' }
                    : getNotRequested(),
                },
                kind: 'email-notification',
              }
            : { kind: 'no-action' },
      };
    case ActionKeys.USER_EMAIL_VERIFICATION_SUCCESS:
      if (state.orderAction.kind !== 'checkout') return state;
      if (state.orderAction.preCheckoutQueue[0]?.kind !== 'email-verification') return state;

      return {
        ...state,
        orderAction: {
          ...state.orderAction,
          preCheckoutQueue: [
            ...state.orderAction.preCheckoutQueue.slice(1), // Pop queue
          ],
        },
      };
    case ActionKeys.CREATE_CARRIER_ACCOUNT_SUCCESS:
      if (state.orderAction.kind !== 'checkout') return state;
      // Add additional carrier activations when implented so the associated reg modals close
      if (
        ![
          'canada_post-activation',
          'dhl_express-activation',
          'royal_mail_sf-activation',
          'ups-activation',
        ].includes(state.orderAction.preCheckoutQueue[0]?.kind)
      )
        return state;

      return {
        ...state,
        orderAction: {
          ...state.orderAction,
          preCheckoutQueue: [
            ...state.orderAction.preCheckoutQueue.slice(1), // Pop queue
          ],
        },
      };
    case ActionKeys.ORDERS_BATCH_CREATE_AND_BUY:
      if (state.orderAction.kind !== 'checkout') return state;
      if (state.orderAction.currentStep.kind !== 'confirmation') return state;

      return {
        ...state,
        orderAction: {
          ...state.orderAction,
          currentStep: {
            ...state.orderAction.currentStep,
            request: getRequesting(),
          },
        },
      };
    case ActionKeys.BATCH_CREATE_ERROR:
    case ActionKeys.ORDERS_BATCH_CREATE_TRANSACTIONS_ERROR:
      if (state.orderAction.kind !== 'checkout') return state;
      if (state.orderAction.currentStep.kind !== 'confirmation') return state;

      return {
        ...state,
        orderAction: {
          ...state.orderAction,
          currentStep: {
            ...state.orderAction.currentStep,
            request: getFailed(action.payload.error!),
          },
        },
      };
    case ActionKeys.ORDERS_CHECKOUT_CONFIRM:
      if (state.orderAction.kind !== 'checkout') return state;

      return {
        ...state,
        orderAction: {
          ...state.orderAction,
          currentStep: {
            documents: action.payload.hasMergedDocuments
              ? { kind: 'documents-merged', labels: getNotRequested() }
              : { kind: 'documents', labels: getNotRequested(), packingSlips: getNotRequested() },
            kind: 'download-labels',
            notifications: action.payload.hasAutomatedEmailNotifications
              ? { kind: 'automated-notifications' }
              : getNotRequested(),
          },
        },
      };
    case ActionKeys.ORDERS_CHECKOUT_CONFIRM_WITH_ERROR:
      if (state.orderAction.kind !== 'checkout') return state;

      return {
        ...state,
        orderAction: {
          ...state.orderAction,
          currentStep: {
            documents: action.payload.hasMergedDocuments
              ? { kind: 'documents-merged', labels: getNotRequested() }
              : { kind: 'documents', labels: getNotRequested(), packingSlips: getNotRequested() },
            kind: 'download-labels-with-batch-errors',
            notifications: action.payload.hasAutomatedEmailNotifications
              ? { kind: 'automated-notifications' }
              : getNotRequested(),
          },
        },
      };
    case ActionKeys.ORDERS_CHECKOUT_SEND_EMAIL_NOTIFICATIONS:
      if (state.orderAction.kind !== 'checkout') return state;
      // We let users download labels, slips and send notifications in two cases
      // Either all good or if there is a batch error ( some of the transactions fails)
      if (
        state.orderAction.currentStep.kind !== 'download-labels-with-batch-errors' &&
        state.orderAction.currentStep.kind !== 'download-labels'
      )
        return state;

      return {
        ...state,
        orderAction: {
          ...state.orderAction,
          currentStep: {
            ...state.orderAction.currentStep,
            notifications: getRequesting(),
          },
        },
      };
    case ActionKeys.ORDERS_CHECKOUT_SEND_EMAIL_NOTIFICATIONS_SUCCESS:
      if (
        state.orderAction.kind !== 'checkout' &&
        (state.orderAction as CheckoutForNotification).kind !== 'email-notification'
      )
        return state;
      if (
        ((state.orderAction as Checkout).currentStep as DownloadLabels).kind !==
          'download-labels' &&
        ((state.orderAction as CheckoutForNotification).currentStep as EmailNotification).kind !==
          'email-notification'
      )
        return state;
      return {
        ...state,
        orderAction: {
          ...state.orderAction,
          currentStep: {
            ...(state.orderAction as CheckoutForNotification).currentStep,
            notifications: getSuccess(null),
          } as EmailNotification,
        } as CheckoutForNotification,
      };

    case ActionKeys.ORDERS_CHECKOUT_SEND_EMAIL_NOTIFICATIONS_ERROR:
      if (
        state.orderAction.kind !== 'checkout' &&
        (state.orderAction as CheckoutForNotification).kind !== 'email-notification'
      )
        return state;
      if (
        ((state.orderAction as Checkout).currentStep as DownloadLabels).kind !==
          'download-labels' &&
        ((state.orderAction as CheckoutForNotification).currentStep as EmailNotification).kind !==
          'email-notification'
      )
        return state;
      return {
        ...state,
        orderAction: {
          ...state.orderAction,
          currentStep: {
            ...(state.orderAction as CheckoutForNotification).currentStep,
            notifications: getFailed(action.payload.error),
          } as EmailNotification,
        } as CheckoutForNotification,
      };

    case ActionKeys.ORDERS_BATCH_GENERATE_PACKING_SLIPS:
      if (state.orderAction.kind !== 'checkout') return state;
      // We let users download labels, slips and send notifications in two cases
      // Either all good or if there is a batch error ( some of the transactions fails)
      if (
        state.orderAction.currentStep.kind !== 'download-labels-with-batch-errors' &&
        state.orderAction.currentStep.kind !== 'download-labels'
      )
        return state;
      if (state.orderAction.currentStep.documents.kind === 'documents-merged') return state;

      return {
        ...state,
        orderAction: {
          ...state.orderAction,
          currentStep: {
            ...state.orderAction.currentStep,
            documents: {
              ...state.orderAction.currentStep.documents,
              packingSlips: getRequesting(),
            },
          },
        },
      };
    case ActionKeys.ORDERS_BATCH_GENERATE_PACKING_SLIPS_SUCCESS:
      if (state.orderAction.kind !== 'checkout') return state;
      // We let users download labels, slips and send notifications in two cases
      // Either all good or if there is a batch error ( some of the transactions fails)
      if (
        state.orderAction.currentStep.kind !== 'download-labels-with-batch-errors' &&
        state.orderAction.currentStep.kind !== 'download-labels'
      )
        return state;
      if (state.orderAction.currentStep.documents.kind === 'documents-merged') return state;

      return {
        ...state,
        orderAction: {
          ...state.orderAction,
          currentStep: {
            ...state.orderAction.currentStep,
            documents: {
              ...state.orderAction.currentStep.documents,
              packingSlips: getSuccess(getLabelUrl(action.payload.slipUrl)),
            },
          },
        },
      };
    case ActionKeys.ORDERS_BATCH_GENERATE_PACKING_SLIPS_ERROR:
      if (state.orderAction.kind !== 'checkout') return state;

      // We let users download labels, slips and send notifications in two cases
      // Either all good or if there is a batch error ( some of the transactions fails)
      if (
        state.orderAction.currentStep.kind !== 'download-labels-with-batch-errors' &&
        state.orderAction.currentStep.kind !== 'download-labels'
      )
        return state;
      if (state.orderAction.currentStep.documents.kind === 'documents-merged') return state;

      return {
        ...state,
        orderAction: {
          ...state.orderAction,
          currentStep: {
            ...state.orderAction.currentStep,
            documents: {
              ...state.orderAction.currentStep.documents,
              packingSlips: getFailed(action.payload.error),
            },
          },
        },
      };
    case ActionKeys.ORDERS_BATCH_GENERATE_ALL_LABELS:
      if (state.orderAction.kind !== 'checkout') return state;
      // We let users download labels, slips and send notifications in two cases
      // Either all good or if there is a batch error ( some of the transactions fails)
      if (
        state.orderAction.currentStep.kind !== 'download-labels-with-batch-errors' &&
        state.orderAction.currentStep.kind !== 'download-labels'
      )
        return state;

      return {
        ...state,
        orderAction: {
          ...state.orderAction,
          currentStep: {
            ...state.orderAction.currentStep,
            documents: {
              ...state.orderAction.currentStep.documents,
              labels: getRequesting(),
            },
          },
        },
      };
    case ActionKeys.ORDERS_BATCH_GENERATE_ALL_LABELS_SUCCESS:
      if (state.orderAction.kind !== 'checkout') return state;
      // We let users download labels, slips and send notifications in two cases
      // Either all good or if there is a batch error ( some of the transactions fails)
      if (
        state.orderAction.currentStep.kind !== 'download-labels-with-batch-errors' &&
        state.orderAction.currentStep.kind !== 'download-labels'
      )
        return state;

      return {
        ...state,
        orderAction: {
          ...state.orderAction,
          currentStep: {
            ...state.orderAction.currentStep,
            documents: {
              ...state.orderAction.currentStep.documents,
              labels: getSuccess(getLabelUrl(action.payload.labelsUrl)),
            },
          },
        },
        selectedOrders: {},
      };
    case ActionKeys.ORDERS_BATCH_GENERATE_ALL_LABELS_ERROR:
      if (state.orderAction.kind !== 'checkout') return state;
      // We let users download labels, slips and send notifications in two cases
      // Either all good or if there is a batch error ( some of the transactions fails)
      if (
        state.orderAction.currentStep.kind !== 'download-labels-with-batch-errors' &&
        state.orderAction.currentStep.kind !== 'download-labels'
      )
        return state;

      return {
        ...state,
        orderAction: {
          ...state.orderAction,
          currentStep: {
            ...state.orderAction.currentStep,
            documents: {
              ...state.orderAction.currentStep.documents,
              labels: getFailed(action.payload.error),
            },
          },
        },
      };

    case ActionKeys.ORDERS_UPDATE_PARCEL:
    case ActionKeys.ORDERS_PATCH_BATCH_ENTRIES:
    case ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_ADDRESS:
    case ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_EXTRAS:
    case ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS:
    case ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SERVICE_LEVEL_TOKEN:
    case ActionKeys.LINE_ITEM_CREATE:
    case ActionKeys.LINE_ITEM_EDIT:
    case ActionKeys.LINE_ITEMS_DELETE:
      return {
        ...state,
        isUpdatingBatchEntry: true,
        isUpdatingParcel: isUpdatingParcel(state, action),
        patchBatchEntriesExtrasRequest:
          action.type === ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS
            ? getRequesting()
            : state.patchBatchEntriesExtrasRequest,
        patchBatchEntriesRequest:
          action.type === ActionKeys.ORDERS_PATCH_BATCH_ENTRIES
            ? getRequesting()
            : state.patchBatchEntriesRequest,
      };

    case ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_ADDRESS_SUCCESS:
      const ordersWithNewBatchEntries: Record<string, number[]> = {};
      action.payload.batchEntries.forEach((batchEntry) => {
        // if a selected order had no batch entries, and batch entries have just been created for that order
        // by adding packages, select all of the new batch entries
        if (state.selectedOrders[batchEntry.orderObjectId]?.length === 0) {
          ordersWithNewBatchEntries[batchEntry.orderObjectId] =
            ordersWithNewBatchEntries[batchEntry.orderObjectId] || [];
          ordersWithNewBatchEntries[batchEntry.orderObjectId].push(batchEntry.id);
        }
      });

      return {
        ...state,
        isUpdatingBatchEntry: false,
        selectedOrders: { ...state.selectedOrders, ...ordersWithNewBatchEntries },
      };

    case ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_ADDRESS_ERROR:
      return {
        ...state,
        isUpdatingBatchEntry: false,
      };
    case ActionKeys.CUSTOMS_ITEM_CREATE:
      return {
        ...state,
        isCreatingCustomsItems: true,
      };
    case ActionKeys.CUSTOMS_ITEM_CREATE_SUCCESS:
      return {
        ...state,
        isCreatingCustomsItems: false,
      };
    case ActionKeys.CUSTOMS_ITEM_UPDATE:
      return {
        ...state,
        isUpdatingCustomsItems: true,
      };
    case ActionKeys.CUSTOMS_ITEM_UPDATE_SUCCESS:
      return {
        ...state,
        isUpdatingCustomsItems: false,
      };
    case ActionKeys.ORDERS_UPDATE_PARCEL_ERROR:
    case ActionKeys.ORDERS_PATCH_BATCH_ENTRIES_ERROR:
    case ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS_ERROR:
    case ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS_RESET:
    case ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SERVICE_LEVEL_TOKEN_SUCCESS:
    case ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SERVICE_LEVEL_TOKEN_ERROR:
    case ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SHIPMENT_DATE_SUCCESS:
    case ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SHIPMENT_DATE_ERROR:
    case ActionKeys.CUSTOMS_ITEM_UPDATE_ERROR:
    case ActionKeys.CUSTOMS_ITEM_CREATE_ERROR:
    case ActionKeys.LINE_ITEM_CREATE_SUCCESS:
    case ActionKeys.LINE_ITEM_CREATE_ERROR:
    case ActionKeys.LINE_ITEM_EDIT_SUCCESS:
    case ActionKeys.LINE_ITEM_EDIT_ERROR:
    case ActionKeys.LINE_ITEMS_DELETE_SUCCESS:
    case ActionKeys.LINE_ITEMS_DELETE_ERROR:
      return {
        ...state,
        autoSaveErrors:
          ActionKeys.ORDERS_UPDATE_PARCEL_ERROR === action.type
            ? {
                ...state.autoSaveErrors,
                ...Object.keys(state.selectedOrders).reduce((current, orderId) => {
                  current[orderId] = true;
                  return current;
                }, {}),
              }
            : state.autoSaveErrors,
        isUpdatingBatchEntry: false,
        isUpdatingParcel: false,
        patchBatchEntriesExtrasRequest:
          action.type === ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS_ERROR
            ? getFailed(action.payload.error)
            : action.type === ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS_RESET
            ? getNotRequested()
            : state.patchBatchEntriesExtrasRequest,
        patchBatchEntriesRequest:
          action.type === ActionKeys.ORDERS_PATCH_BATCH_ENTRIES_ERROR
            ? getFailed(
                action.payload.err?.message ||
                  action.payload.err?.toString() ||
                  'An error occurred',
              )
            : state.patchBatchEntriesRequest,
      };

    case ActionKeys.ORDER_SPLIT_MODAL_OPEN:
      return {
        ...state,
        orderAction: {
          kind: 'splitting',
          orderObjectId: action.payload.orderObjectId,
          status: getNotRequested(),
        },
      };
    case ActionKeys.ORDER_SPLIT_MODAL_CLOSE:
      return {
        ...state,
        orderAction: { ...initialState.orderAction },
      };
    case ActionKeys.ORDER_SCREEN_OPEN:
      return {
        ...state,
        isSingleOrderMode: true,
      };
    case ActionKeys.ORDER_SCREEN_CLOSE:
      return {
        ...state,
        isSingleOrderMode: false,
        orderAction: { ...initialState.orderAction },
      };
    case ActionKeys.ORDER_CREATE_PARTIAL_FULFILLMENT_REQUESTS: {
      return {
        ...state,
        orderAction: {
          kind: 'splitting',
          orderObjectId: action.payload.orderObjectId,
          status: getRequesting(),
        },
      };
    }
    case ActionKeys.ORDER_CREATE_PARTIAL_FULFILLMENT_REQUESTS_SUCCESS:
      const hasSelectedOrder = state.selectedOrders[action.payload.order.objectId]?.length >= 0;

      const { isUndo } = action.payload;
      const isReturn = action.payload.order.partialFulfillmentRequests.some(
        ({ isReturn }) => isReturn,
      );

      return {
        ...state,
        data: {
          ...state.data,
          [action.payload.order.objectId]: action.payload.order,
        },
        orderAction: {
          kind: isUndo || isReturn ? 'no-action' : 'splitting',
          orderObjectId: action.payload.order.objectId,
          status: getSuccess(null),
        },
        selectedOrders: hasSelectedOrder
          ? {
              ...state.selectedOrders,
              [action.payload.order.objectId]: hasSelectedOrder
                ? (state.selectedOrders[action.payload.order.objectId]
                    ?.reduce((ranks: number[], batchEntryId) => {
                      const batchEntry = state.data[
                        action.payload.order.objectId
                      ].partialFulfillmentRequests.find((be) => be.id === batchEntryId);
                      if (batchEntry) return [...ranks, batchEntry.partialFulfillmentRank];

                      return ranks;
                    }, [])
                    .map((rank) => {
                      const batchEntryId = action.payload.order.partialFulfillmentRequests.find(
                        (be) => be.partialFulfillmentRank === rank,
                      )?.id;
                      return batchEntryId;
                    })
                    .filter((x) => x) as number[])
                : [],
            }
          : state.selectedOrders,
      };
    case ActionKeys.ORDER_CREATE_PARTIAL_FULFILLMENT_REQUESTS_ERROR:
      return {
        ...state,
        orderAction: {
          kind: 'splitting',
          orderObjectId: action.payload.orderObjectId,
          status: getFailed(action.payload.error),
        },
      };
    case ActionKeys.USER_SET_VERIFICATION_STATUS: {
      const { idVerification } = action.payload;

      switch (idVerification.state) {
        case IDVerificationStatus.VERIFICATION_NEEDED:
          return {
            ...state,
            orderAction: {
              kind: 'id-verification',
              status: getNotRequested(),
            },
          };
        case IDVerificationStatus.VERIFICATION_SUBMITTED:
          return {
            ...state,
            orderAction: {
              kind: 'id-verification',
              status: getSuccess(null),
            },
          };
        case IDVerificationStatus.VERIFICATION_PROCESSING:
          return {
            ...state,
            orderAction: {
              kind: 'id-verification',
              status: getRequesting(),
            },
          };
        case IDVerificationStatus.VERIFICATION_FAILED_ALLOW_RETRY:
          return {
            ...state,
            orderAction: {
              kind: 'id-verification',
              status: getFailed({ codes: idVerification.errors, retryable: true }),
            },
          };
        case IDVerificationStatus.VERIFICATION_FAILED_NO_RETRY:
          return {
            ...state,
            orderAction: {
              kind: 'id-verification',
              status: getFailed({ codes: idVerification.errors, retryable: false }),
            },
          };
        default:
          return state;
      }
    }
    case ActionKeys.USER_CLOSE_VERIFICATION_MODAL: {
      if (state.orderAction.kind !== 'id-verification') return state;

      return {
        ...state,
        orderAction: { kind: 'no-action' },
      };
    }
    case ActionKeys.ORDER_BEGIN_EDIT_ACTION: {
      const { orderSelection } = action.payload;

      if (orderSelection) {
        return {
          ...state,
          orderAction: {
            kind: 'order-edit',
            orderSelection,
          },
        };
      }
      return state;
    }
    case ActionKeys.ORDER_END_EDIT_ACTION: {
      return {
        ...state,
        orderAction: { kind: 'no-action' },
      };
    }
    case ActionKeys.ORDERS_SET_CHECKOUT_RATE_AFTER_POLL: {
      const key = checkoutRateAfterPollKey(action.payload.prevBatchEntry);
      return {
        ...state,
        checkoutRateAfterPoll: {
          ...state.checkoutRateAfterPoll,
          [key]: {
            autoStartCheckout: action.payload.autoStartCheckout,
            carrierAccount: action.payload.carrierAccount,
            currBatchEntry: null,
            prevBatchEntry: action.payload.prevBatchEntry,
            serviceLevelToken: action.payload.serviceLevelToken,
          },
        },
      };
    }
    case ActionKeys.ORDER_BATCH_ENTRIES_FETCH_SUCCESS: {
      if (!state.checkoutRateAfterPoll) return state;

      const { batchEntries } = action.payload;

      const batchEntriesByOrderObjectId = batchEntries.reduce<Record<string, BatchEntryExtended[]>>(
        (acc, be) => {
          (acc[be.orderObjectId] ??= []).push(be);
          return acc;
        },
        {},
      );

      let didUpdate = false;
      const nextCheckoutRateAfterPoll = Object.entries(
        state.checkoutRateAfterPoll,
      ).reduce<CheckoutRatesAfterPoll>((accum, [key, entry]) => {
        const { orderObjectId, partialFulfillmentRank } = entry.prevBatchEntry;
        const sameOrderEntries = batchEntriesByOrderObjectId[orderObjectId] ?? [];
        const match =
          sameOrderEntries.find((be) => be.partialFulfillmentRank === partialFulfillmentRank) ??
          (sameOrderEntries.length === 1 ? sameOrderEntries[0] : undefined);

        if (match && (match.shipmentObjectId || !entry.currBatchEntry)) {
          didUpdate = true;
          accum[key] = { ...entry, currBatchEntry: match };
        } else {
          accum[key] = entry;
        }

        return accum;
      }, {});

      return didUpdate ? { ...state, checkoutRateAfterPoll: nextCheckoutRateAfterPoll } : state;
    }
    case ActionKeys.ORDERS_REMOVE_CHECKOUT_RATE_AFTER_POLL_ENTRY: {
      if (!state.checkoutRateAfterPoll) return state;

      const { [action.payload.key]: _removed, ...rest } = state.checkoutRateAfterPoll;

      return {
        ...state,
        checkoutRateAfterPoll: Object.keys(rest).length ? rest : null,
      };
    }
    case ActionKeys.ORDERS_START_CHECKOUT:
      return {
        ...state,
        checkoutRateAfterPoll: action.payload.preserveRateAfterPoll
          ? state.checkoutRateAfterPoll
          : null,
        isSidePanelCheckout: !!action.payload.isSourceSidePanel,
      };
    case ActionKeys.ORDERS_CLEAR_AUTO_SAVE_ERRORS:
      return {
        ...state,
        autoSaveErrors: {},
      };
    case ActionKeys.ORDERS_UPDATE_TAGS_SUCCESS:
      const ordersWithTags = Object.keys(action.payload.orderTags).reduce((orderTags, orderId) => {
        if (!state.data[orderId]) return orderTags;

        const alphabeticallySortedTags = sortOrderTagsByName(action.payload.orderTags[orderId]);

        return {
          ...orderTags,
          [orderId]: {
            ...state.data[orderId],
            tags: alphabeticallySortedTags,
          },
        };
      }, {});

      return {
        ...state,
        data: {
          ...state.data,
          ...ordersWithTags,
        },
        orderTagsBeingUpdated: [],
      };
    case ActionKeys.ORDERS_UPDATE_TAGS:
      return {
        ...state,
        orderTagsBeingUpdated: action.payload.orderIds,
      };
    case ActionKeys.ORDERS_UPDATE_TAGS_ERROR:
      return {
        ...state,
        orderTagsBeingUpdated: [],
      };
    case ActionKeys.ORDERS_UPDATE_TAGS_CLEAR:
      return {
        ...state,
        orderTagsBeingUpdated: [],
      };
    case ActionKeys.ORDERS_FETCH_TAGS:
      return {
        ...state,
        isLoadingOrderTags: true,
      };
    case ActionKeys.ORDERS_FETCH_TAGS_SUCCESS:
      return {
        ...state,
        isLoadingOrderTags: false,
      };
    case ActionKeys.ORDERS_FETCH_TAGS_ERROR:
      return {
        ...state,
        isLoadingOrderTags: false,
      };

    case ActionKeys.ORDER_CREATE_RETURN_BATCH_ENTRY:
      return {
        ...state,
        createReturnBatchEntryHasError: false,
        isCreatingReturnBatchEntry: true,
        returnLabelCreatedSuccessfully: false,
      };

    case ActionKeys.ORDER_CREATE_RETURN_BATCH_ENTRY_SUCCESS:
      return {
        ...state,
        createReturnBatchEntryHasError: false,
        isCreatingReturnBatchEntry: false,
        returnLabelCreatedSuccessfully: true,
      };

    case ActionKeys.ORDER_CREATE_RETURN_LABEL_ERROR:
      return {
        ...state,
        createReturnBatchEntryHasError: true,
        isCreatingReturnBatchEntry: false,
        returnLabelCreatedSuccessfully: false,
      };

    default:
      return state;
  }
};

export default reducer;
